// Topic-based fallback images for conference cards.
// All images are free, high-quality Unsplash photos served via the optimized
// images.unsplash.com endpoint. We deterministically pick one per conference id
// so cards look varied across the catalog yet stable across reloads.

const POOLS = {
  ai: [
    "photo-1677442136019-21780ecad995", // neural net
    "photo-1620712943543-bcc4688e7485", // robot
    "photo-1655720828018-edd2daec9349", // ai chip
  ],
  computing: [
    "photo-1518770660439-4636190af475", // green circuit
    "photo-1517336714731-489689fd1ca8", // server room
    "photo-1581090700227-1e37b190418e", // motherboard
  ],
  biology: [
    "photo-1530026405186-ed1f139313f8", // DNA helix
    "photo-1583912267550-d6c2ac3196c0", // microscope
    "photo-1606206522398-de8a8b04c8b9", // lab
  ],
  medicine: [
    "photo-1551601651-2a8555f1a136", // stethoscope
    "photo-1576091160550-2173dba999ef", // medical
    "photo-1579684385127-1ef15d508118", // surgery
  ],
  physics: [
    "photo-1635070041078-e363dbe005cb", // galaxy
    "photo-1451187580459-43490279c0fa", // earth from space
    "photo-1462331940025-496dfbfc7564", // lab
  ],
  chemistry: [
    "photo-1532187863486-abf9dbad1b69", // beakers
    "photo-1581093588401-fbb62a02f120", // molecules
    "photo-1554475901-4538ddfbccc2", // chem lab
  ],
  business: [
    "photo-1556761175-5973dc0f32e7", // boardroom
    "photo-1540575467063-178a50c2df87", // conference
    "photo-1521737604893-d14cc237f11d", // team meeting
  ],
  finance: [
    "photo-1611974789855-9c2a0a7236a3", // wall st
    "photo-1590283603385-17ffb3a7f29f", // charts
    "photo-1554224155-6726b3ff858f", // ledger
  ],
  design: [
    "photo-1561070791-2526d30994b8", // sketches
    "photo-1558655146-9f40138edfeb", // ui sketch
    "photo-1492691527719-9d1e07e534b4", // pens
  ],
  music: [
    "photo-1511671782779-c97d3d27a1d4", // concert
    "photo-1493225457124-a3eb161ffa5f", // mixer
    "photo-1514525253161-7a46d19cd819", // crowd
  ],
  policy: [
    "photo-1529107386315-e1a2ed48a620", // capitol
    "photo-1605108042553-37f8d83cbc4a", // un flags
    "photo-1583468982228-19f19164aee2", // gavel
  ],
  climate: [
    "photo-1497436072909-60f360e1d4b1", // forest
    "photo-1532601224476-15c79f2f7a51", // wind turbines
    "photo-1473773508845-188df298d2d1", // ice
  ],
  default: [
    "photo-1540575467063-178a50c2df87", // big conference
    "photo-1505373877841-8d25f7d46678", // audience
    "photo-1556761175-b413da4baf72", // panel
    "photo-1559223607-a43c990c692c", // mic at conference
  ],
};

const TOPIC_RULES = [
  { match: /(ai|artificial|machine learning|ml|nlp|robotics|deep learning|neural|llm)/i, key: "ai" },
  { match: /(comput|software|systems|hci|chi|security|cryptography|database|network|hpc|algorith|distributed)/i, key: "computing" },
  { match: /(bio|genetic|genom|protein|cell|life science|neurosci|ecology|biotech)/i, key: "biology" },
  { match: /(medic|health|clinic|surger|oncolog|pediatr|cardio|dental|nurs|pharma|hospital|disease)/i, key: "medicine" },
  { match: /(physics|astro|cosmolog|quantum|particle|optic|gravity)/i, key: "physics" },
  { match: /(chem|material|polymer|catalys|nano)/i, key: "chemistry" },
  { match: /(business|management|entrepre|leadership|innovation|strategy|hr|operations|industry)/i, key: "business" },
  { match: /(finan|econ|invest|banking|fintech|account|trading|market)/i, key: "finance" },
  { match: /(design|ux|ui|graphic|typograph|architect|art)/i, key: "design" },
  { match: /(music|audio|sound|acoustic)/i, key: "music" },
  { match: /(law|policy|legal|govern|political|ethic|justice)/i, key: "policy" },
  { match: /(climate|environment|sustain|energy|renewable|ecolog|ocean|forest|agricult)/i, key: "climate" },
];

function topicKey(topic) {
  const t = (topic || "").toString();
  for (const r of TOPIC_RULES) if (r.match.test(t)) return r.key;
  return "default";
}

function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

// Public: returns a stable image URL for a conference card.
export function confImage(conf, opts = {}) {
  if (conf?.image_url) return conf.image_url;
  const { w = 1200, h = 720 } = opts;
  const pool = POOLS[topicKey(conf?.topic)] || POOLS.default;
  const seed = hashStr((conf?.id || conf?.title || "x") + (conf?.topic || ""));
  const photoId = pool[seed % pool.length];
  return `https://images.unsplash.com/${photoId}?auto=format&fit=crop&w=${w}&h=${h}&q=70`;
}

// Public: tiny version for sidebar/list thumbnails.
export function confThumb(conf) {
  return confImage(conf, { w: 600, h: 360 });
}
